import React, { useState, useEffect, useRef } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CASE_STUDIES, CaseStudy } from '../data/cases.data';
import { useOgImage } from '../hooks/useOgImage';

interface CaseCarouselProps {
  autoplayIntervalMs?: number;
  loop?: boolean;
}

const CaseCarousel: React.FC<CaseCarouselProps> = ({ 
  autoplayIntervalMs = 5000, 
  loop = true 
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const intervalRef = useRef<number | null>(null);

  // Filtrar casos ocultos
  const visibleCases = CASE_STUDIES.filter(caseStudy => !caseStudy.hidden);

  // Navegación automática
  useEffect(() => {
    if (!isAutoPlaying || isDragging) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        if (loop) {
          return prevIndex === visibleCases.length - 1 ? 0 : prevIndex + 1;
        }
        return Math.min(prevIndex + 1, visibleCases.length - 1);
      });
    }, autoplayIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, isDragging, autoplayIntervalMs, loop, visibleCases.length]);

  // Pausar cuando la página no está visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsAutoPlaying(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Reanudar auto-play después de 5 segundos
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const nextSlide = () => {
    const nextIndex = loop
      ? (currentIndex + 1) % visibleCases.length
      : Math.min(currentIndex + 1, visibleCases.length - 1);
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = loop
      ? (currentIndex - 1 + visibleCases.length) % visibleCases.length
      : Math.max(currentIndex - 1, 0);
    goToSlide(prevIndex);
  };

  const handleDragEnd = (_: any, info: PanInfo) => {
    setIsDragging(false);
    const threshold = 50;
    
    if (info.offset.x > threshold) {
      prevSlide();
    } else if (info.offset.x < -threshold) {
      nextSlide();
    }
  };

  return (
    <section 
      className="py-6"
      aria-label="Casos de éxito"
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Título de la sección */}
        <div className="text-center mb-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--stpp-black)' }}>
            {t('cases.title')}
          </h2>
          <p className="text-xl" style={{ color: 'var(--stpp-muted)' }}>
            {t('cases.subtitle')}
          </p>
        </div>

        {/* Carrusel */}
        <div className="relative">
          {/* Controles de navegación */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={prevSlide}
              className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
              aria-label="Caso anterior"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex space-x-3">
              {visibleCases.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300 ${
                    index === currentIndex 
                      ? 'w-6 bg-white' 
                      : 'w-2 bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Ir al caso ${index + 1}`}
                  aria-current={index === currentIndex ? 'true' : 'false'}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
              aria-label="Siguiente caso"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Slides */}
          <div className="relative overflow-hidden rounded-2xl">
            <motion.div
              className="flex"
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={handleDragEnd}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              {visibleCases.map((caseStudy) => (
                <CaseSlide key={caseStudy.title} caseStudy={caseStudy} />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Componente individual para cada slide
const CaseSlide: React.FC<{ caseStudy: CaseStudy }> = ({ caseStudy }) => {
  const { t } = useTranslation();
  const { imageUrl, loading } = useOgImage(caseStudy.url);
  
  // Obtener la descripción traducida
  const translatedDescription = caseStudy.translationKey 
    ? t(`cases.descriptions.${caseStudy.translationKey}`)
    : caseStudy.description;

  const resolveAssetUrl = (path?: string): string | null => {
    if (!path) return null;
    // Si viene de src/img (ej: "/src/img/et.png"), resolver con import.meta.url para que Vite la incluya en el bundle
    if (path.startsWith('/src/')) {
      try {
        const relative = path.replace('/src/', '../'); // desde components/ a img/
        return new URL(relative, import.meta.url).href;
      } catch {
        return null;
      }
    }
    return path;
  };

  const staticImageUrl = resolveAssetUrl(caseStudy.imageUrl);
  const finalImageUrl = staticImageUrl || imageUrl || '/og-fallback.jpg';

  return (
    <div className="w-full flex-shrink-0">
      {/* Contenedor cuadrado/rectangular más alto */}
      <div className="relative w-full aspect-[1/1] md:aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {loading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse" />
        ) : (
          <div className="w-full h-full flex items-center justify-center p-6 md:p-10">
            <img
              src={finalImageUrl || ''}
              alt={caseStudy.title}
              className="max-h-full max-w-full object-contain"
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* Texto por debajo */}
      <div className="px-2 sm:px-4 md:px-6 mt-4 md:mt-5">
        <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" style={{ color: 'var(--stpp-black)' }}>
          {caseStudy.title}
        </h3>
        <p className="text-gray-600 text-sm md:text-base mb-3 max-w-3xl">
          {translatedDescription}
        </p>
        <a
          href={caseStudy.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-emerald-700 hover:text-emerald-600 underline underline-offset-4 transition-colors duration-200"
        >
          {t('cases.visitProject')}
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default CaseCarousel;
